// Oike Acompañamiento — what the coach needs to act on a process, computed from
// the coach's process view (shared/coachingData.ts › coachProcessView): the
// next action, the progress of the sessions and the journey of sessions,
// activities and shared progress, in order. Pure: no network, no AI.
// Progress counts sessions only; there are no scores, rankings or streaks.

const ts = (iso) => (iso ? new Date(iso).getTime() : NaN);
const sessionLabel = (s) => `sesión ${s.number || ''}`.trim();
const dayLabel = (d) => { try { return new Date(`${d}T12:00:00Z`).toLocaleDateString('es', { day: 'numeric', month: 'short', timeZone: 'UTC' }); } catch { return d; } };

/** Sessions that count for the progress: not cancelled; in a team, the group ones. */
export function countedSessions(view) {
  const team = view.process?.modality === 'team';
  return (view.sessions || []).filter(s => s.status !== 'cancelled' && (!team || s.kind === 'group'));
}

export function sessionProgress(view) {
  const list = countedSessions(view);
  const done = list.filter(s => s.status === 'done').length;
  const total = list.length;
  return { done, total, halfway: total > 1 && done > 0 && done * 2 >= total, finished: total > 0 && done === total };
}

/**
 * The one next thing to do, in this order: the first agreement, a summary
 * waiting for approval, the next scheduled session, the next session to
 * schedule; otherwise, closing or agreeing next steps.
 * Returns { title, detail, go: { tab, sessionId? } }.
 */
export function nextAction(view, now = new Date()) {
  const p = view.process || {};
  const sessions = (view.sessions || []).filter(s => s.status !== 'cancelled');
  if (!p.focusAgreed) return { title: 'Registrar el acuerdo inicial', detail: 'El foco queda como propuesto hasta registrar el acuerdo de la primera sesión.', go: { tab: 'agreement' } };
  const draft = sessions.find(s => s.status === 'done' && s.summaryStatus === 'draft');
  if (draft) return { title: `Revisar el resumen de la ${sessionLabel(draft)}`, detail: 'El borrador no se comparte hasta que lo apruebes.', go: { tab: 'sessions', sessionId: draft.id } };
  const upcoming = sessions.filter(s => s.status === 'scheduled' && ts(s.startsAt) >= now.getTime() - 3 * 3600e3).sort((a, b) => ts(a.startsAt) - ts(b.startsAt))[0];
  if (upcoming) return { title: `Preparar la ${sessionLabel(upcoming)}`, detail: upcoming.title || '', when: upcoming.startsAt, go: { tab: 'sessions', sessionId: upcoming.id } };
  const toSchedule = sessions.filter(s => s.status === 'unscheduled').sort((a, b) => (a.number || 99) - (b.number || 99))[0];
  if (toSchedule) return { title: `Agendar la ${sessionLabel(toSchedule)}`, detail: toSchedule.title || '', go: { tab: 'sessions', sessionId: toSchedule.id } };
  return { title: 'Acordar los próximos pasos', detail: 'No quedan sesiones por agendar en este proceso.', go: { tab: 'agreement' } };
}

/** Sessions, activities and shared progress in one list, dated ones first in time order, then what is still ahead. */
export function journey(view, memberName = () => '') {
  const team = view.process?.modality === 'team';
  const items = [];
  for (const s of view.sessions || []) {
    if (s.status === 'cancelled') continue;
    items.push({
      id: `s:${s.id}`, type: 'session', sessionId: s.id,
      title: `${s.number ? `Sesión ${s.number}` : 'Sesión'}${s.title ? ` · ${s.title}` : ''}`,
      who: team ? (s.kind === 'group' ? 'grupal' : memberName(s.memberId)) : '',
      date: s.startsAt, state: s.status === 'done' ? 'done' : s.status === 'scheduled' ? 'next' : 'ahead',
      detail: s.status === 'done' ? (s.summaryStatus === 'approved' ? 'Hecha · resumen aprobado' : s.summaryStatus === 'draft' ? 'Hecha · resumen por revisar' : 'Hecha') : s.status === 'scheduled' ? 'Agendada' : 'Sin agendar',
      order: s.number || 0,
    });
  }
  for (const a of view.assignments || []) {
    if (a.status === 'withdrawn') continue;
    items.push({
      id: `a:${a.id}`, type: 'activity', title: a.title || 'Actividad', who: team && a.memberId ? memberName(a.memberId) : team ? 'equipo' : '',
      date: a.doneAt || a.createdAt, state: a.status === 'done' ? 'done' : 'next',
      detail: a.status === 'done' ? 'Completada' : a.dueOn ? `Asignada · para el ${dayLabel(a.dueOn)}` : 'Asignada', order: 0,
    });
  }
  for (const e of view.sharedEntries || []) {
    const text = String(e.text || '').replace(/\s+/g, ' ').trim();
    items.push({
      id: `e:${e.id}`, type: 'progress', title: e.kind === 'progress' ? 'Avance compartido' : e.kind === 'exercise_response' ? 'Respuesta a una actividad' : 'Reflexión compartida', who: team ? memberName(e.memberId) : '',
      date: e.sharedAt || e.createdAt, state: 'done', detail: text.length > 160 ? `${text.slice(0, 157)}…` : text, order: 0,
    });
  }
  const dated = items.filter(i => i.date).sort((a, b) => ts(a.date) - ts(b.date));
  const ahead = items.filter(i => !i.date).sort((a, b) => a.order - b.order);
  return [...dated, ...ahead];
}
